import { AirwallexHorizontalLogo, BlockFilledIcon, DotsFilledIcon } from '@approvalmax/ui';
import { Alert, Box, Button, Dropdown, Flex, Menu, Text } from '@approvalmax/ui/src/components';
import { domHelpers } from '@approvalmax/utils';
import { useAtomValue } from 'jotai';
import { constants } from 'modules/common';
import { memo } from 'react';
import { Link } from 'react-router-dom';
import { getPath, Path } from 'urlBuilder';

import { savingOrganisationState } from '../OrganisationInfo/OrganisationInfo.state';
import { qa } from './AdditionalServiceCardAirwallex.constants';
import { useAdditionalServiceCardAirwallex, useCompanyIntegrationStatus } from './AdditionalServiceCardAirwallex.hooks';
import { messages } from './AdditionalServiceCardAirwallex.messages';
import { AdditionalServiceCardAirwallexProps } from './AdditionalServiceCardAirwallex.types';

const { airwallexConstants } = constants;

export const AdditionalServiceCardAirwallex = memo<AdditionalServiceCardAirwallexProps>((props) => {
    const { company } = props;

    const isSavingOrganisation = useAtomValue(savingOrganisationState);

    const { isIntegrationConnected, isIntegrationDisconnected } = useCompanyIntegrationStatus(company);
    const { isLoading, onConnect, onReconnect, onDisconnect } = useAdditionalServiceCardAirwallex(company);

    if (!company.flags.isAirwallexPaymentAvailable) {
        return null;
    }

    const description =
        company.airwallexIntegration && company.airwallexIntegration.isConnected
            ? messages.connectedDescription
            : messages.disconnectedDescription;

    const operationsDisabled =
        isSavingOrganisation || isLoading || company.flags.isWorkflowManager || company.flags.isAuditor;

    const isConnectAvailable = !company.airwallexIntegration;
    const isConnectDisabled = company.isReadonly || operationsDisabled;
    const isReconnectAvailable = company.airwallexIntegration && !company.airwallexIntegration.isConnected;
    const isReconnectDisabled = company.isReadonly || operationsDisabled;

    const isDisconnectAvailable = company.airwallexIntegration && company.airwallexIntegration.isConnected;
    const isDisconnectDisabled = operationsDisabled;
    const accountName = company.airwallexIntegration?.integratedAccountName || null;
    const accountHolder =
        `${company.airwallexIntegration?.connectedByUser?.firstName} ${company.airwallexIntegration?.connectedByUser?.lastName}`.trim();

    return (
        <Box
            color={isConnectAvailable ? 'silver80' : 'white100'}
            bordered={!isConnectAvailable}
            radius='xsmall'
            spacing='24'
            height='100%'
            aria-label={messages.airwallex}
            data-qa={domHelpers.generateDataQa(qa)}
        >
            <Flex direction='column' spacing='20' height='100%' justifyContent='space-between'>
                <Flex direction='column' spacing='16'>
                    <Flex spacing='16' alignItems='center'>
                        <Flex grow={1}>
                            <AirwallexHorizontalLogo />
                        </Flex>

                        {isConnectAvailable && (
                            <Button
                                size='small'
                                disabled={!isIntegrationConnected || isConnectDisabled}
                                onClick={onConnect}
                                color='brandAWX80'
                            >
                                {messages.connect}
                            </Button>
                        )}

                        {isReconnectAvailable && (
                            <Button
                                size='small'
                                disabled={!isIntegrationConnected || isReconnectDisabled}
                                onClick={onReconnect}
                                color='brandAWX80'
                            >
                                {messages.reconnect}
                            </Button>
                        )}

                        {isDisconnectAvailable && !company.isReadonly && (
                            <Dropdown
                                activator={
                                    <Button icon noPadding variant='text' color='blue100'>
                                        <DotsFilledIcon size={24} />
                                    </Button>
                                }
                                width={150}
                                disabled={isDisconnectDisabled}
                            >
                                <Menu divider size='small'>
                                    <Menu.Item
                                        title={messages.disconnect}
                                        name={messages.disconnect}
                                        onClick={onDisconnect}
                                    />
                                </Menu>
                            </Dropdown>
                        )}

                        {isDisconnectAvailable && (
                            <Text font='label' color='blue100' uppercase>
                                {messages.labelConnected}
                            </Text>
                        )}

                        {isDisconnectAvailable && company.flags.isWorkflowManager && (
                            <Button
                                color='red40'
                                size='small'
                                as={Link}
                                to={getPath(Path.companyWorkflows, company.id)}
                            >
                                {messages.gotoWorkflows}
                            </Button>
                        )}
                    </Flex>

                    {accountName && isIntegrationConnected && company.airwallexIntegration?.isConnected && (
                        <Flex spacing='8'>
                            <Flex spacing='4'>
                                <Text font='body' fontSize='small' color='midnight70'>
                                    {messages.connectedToAirwallex}
                                </Text>

                                <Text as='span' font='body' fontSize='small' fontWeight='bold' color='midnight80'>
                                    {accountName}
                                </Text>
                            </Flex>

                            <Text font='body' fontSize='small' color='midnight70'>
                                {accountHolder}
                            </Text>
                        </Flex>
                    )}

                    <Flex direction='column' spacing='4'>
                        <Text
                            font='body'
                            fontSize='small'
                            color='midnight80'
                            data-qa={domHelpers.generateDataQa(qa, 'description')}
                        >
                            {description}
                        </Text>

                        <Text font='label' fontSize='small'>
                            <a
                                href={airwallexConstants.CONNECT_TO_AIRWALLEX_LEARN_MORE_LINK}
                                target='_blank'
                                rel='noopener noreferrer'
                            >
                                {messages.learnMore}
                            </a>
                        </Text>
                    </Flex>
                </Flex>

                {isIntegrationDisconnected && !isDisconnectAvailable && (
                    <Alert startIcon={<BlockFilledIcon size={24} />} size='small' invalid={true} color='transparent'>
                        {messages.warningConnectDisabled}
                    </Alert>
                )}
            </Flex>
        </Box>
    );
});

AdditionalServiceCardAirwallex.displayName = 'AdditionalServiceCardAirwallex';
